import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogIn, AlertCircle } from 'lucide-react';
import { AUTH_ROLES } from '../../config/authConfig';
import { useAuth } from '../../context/AuthContext';
import JudgePasswordReset from './JudgePasswordReset';
import './LoginNew.css';

const Login = ({ onLoginSuccess, onSelectRole, onShowJudgeRegistration }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [step, setStep] = useState('role'); // 'role' | 'login' | 'password-reset'
  const [selectedRole, setSelectedRole] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Determinar si estamos en una ruta específica
  const isAdminRoute = location.pathname.includes('/admin');
  const isJudgeRoute = location.pathname.includes('/jueces');
  const isResultadosRoute = location.pathname.includes('/resultados');

  // Si la ruta es específica, ir directamente al login
  useEffect(() => {
    if (isAdminRoute) {
      setSelectedRole('admin');
      setStep('login');
    } else if (isJudgeRoute) {
      setSelectedRole('judge');
      setStep('login');
    } else if (isResultadosRoute) {
      // En /resultados no se muestra el login, ya que es público
      // Esto no debería ocurrir, pero como protección extra:
      setStep('role');
    }
  }, [isAdminRoute, isJudgeRoute, isResultadosRoute]);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (role === 'public' || role === 'resultados') {
      // El público no necesita login
      onLoginSuccess({ role: 'public', user: null });
    } else {
      setStep('login');
      setError('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!username || !password) {
      setError('Por favor completa todos los campos');
      setLoading(false);
      return;
    }

    try {
      // Usar Supabase Auth para todos los roles
      const { data, error: authError } = await signIn(username, password);

      if (authError) {
        setError(authError);
        setLoading(false);
        return;
      }

      const userRole = data?.user?.user_metadata?.role;

      if (selectedRole === 'admin' && userRole === 'admin') {
        onLoginSuccess({ role: AUTH_ROLES.ADMIN, user: { id: data.user.id, name: 'Administrador', email: data.user.email } });
      } else if (selectedRole === 'judge' && (userRole === 'judge' || userRole === 'admin')) {
        onLoginSuccess({ role: AUTH_ROLES.JUDGE, user: { id: data.user.id, name: data.user.user_metadata?.name, email: data.user.email } });
      } else {
        setError('No tienes permisos para este rol.');
      }
    } catch (error) {
      console.error('Error en login:', error);
      setError('Error durante la autenticación');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // No permitir volver si estamos en una ruta específica
    if (!isAdminRoute && !isJudgeRoute) {
      setStep('role');
    }
    setUsername('');
    setPassword('');
    setError('');
    if (!isAdminRoute && !isJudgeRoute) {
      setSelectedRole(null);
    }
  };

  if (step === 'role') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="login-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
            <p>Selecciona cómo deseas acceder</p>
          </div>

          <div className="role-selection">
            <button
              onClick={() => handleRoleSelect('public')}
              className="role-btn public-btn"
            >
              <div className="role-icon">👥</div>
              <h3>Visualizar Resultados</h3>
              <p>Ver resultados de la competencia sin login</p>
            </button>

            <button
              onClick={() => handleRoleSelect('judge')}
              className="role-btn judge-btn"
            >
              <div className="role-icon">⚖️</div>
              <h3>Ingreso de Juez</h3>
              <p>Acceso para jueces - Ingresar puntuaciones</p>
            </button>

            <button
              onClick={() => handleRoleSelect('admin')}
              className="role-btn admin-btn"
            >
              <div className="role-icon">🔐</div>
              <h3>Acceso Administrador</h3>
              <p>Panel administrativo completo</p>
            </button>
          </div>

          <div style={{ marginTop: '20px', textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
              ¿Eres un nuevo juez?
            </p>
            <button
              onClick={onShowJudgeRegistration}
              className="role-btn"
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                border: 'none'
              }}
            >
              <div className="role-icon">✍️</div>
              <h3>Registrarse como Juez</h3>
              <p>Crea tu cuenta para empezar</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If password reset step
  if (step === 'password-reset') {
    return (
      <JudgePasswordReset
        onBack={() => setStep('login')}
        onResetSuccess={() => {
          // Después de reset exitoso, volver al login
          setUsername('');
          setPassword('');
          setError('');
          setStep('login');
        }}
      />
    );
  }

  // Login step (admin or judge)
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="login-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
          <h1>
            {selectedRole === 'judge' ? 'Acceso de Juez' : 'Acceso de Administrador'}
          </h1>
          <p>Ingresa tus credenciales</p>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          {error && (
            <div className="error-message">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">
              {selectedRole === 'judge' ? 'Correo Electrónico' : 'Usuario'}
            </label>
            <input
              id="username"
              type={selectedRole === 'judge' ? 'email' : 'text'}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={selectedRole === 'judge' ? 'tu@correo.com' : 'admin'}
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={loading}
              className="form-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="login-submit"
          >
            {loading ? 'Verificando...' : 'Ingresar'}
          </button>

          {selectedRole === 'judge' && (
            <button
              type="button"
              onClick={() => setStep('password-reset')}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px',
                background: 'transparent',
                color: '#8b5cf6',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer',
                textDecoration: 'underline',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = '#7c3aed'}
              onMouseLeave={(e) => e.target.style.color = '#8b5cf6'}
            >
              ¿Olvidaste tu contraseña?
            </button>
          )}
        </form>

        <button
          onClick={handleBack}
          className="back-to-roles-btn"
        >
          ← Volver a seleccionar rol
        </button>
      </div>
    </div>
  );
};

export default Login;
